import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  Table,
  Button,
  Input,
  Select,
  Tag,
  Space,
  message,
  Row,
  Col,
  Statistic,
} from "antd";
import {
  PlusOutlined,
  SearchOutlined,
  EyeOutlined,
  EditOutlined,
  FileTextOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import type { Case } from "../../../shared/types";
import { api } from "../../services/api";
import { useAuth } from "../../stores/auth";
import dayjs from "dayjs";

const statusMap = {
  draft: { color: "default", text: "草稿" },
  pending: { color: "processing", text: "待处理" },
  archived: { color: "success", text: "已归档" },
};

export default function CaseListPage() {
  const [cases, setCases] = useState<Case[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    total: 0,
  });
  const [status, setStatus] = useState<string>("");
  const [keyword, setKeyword] = useState("");
  const navigate = useNavigate();
  const { user } = useAuth();

  const fetchCases = async () => {
    setLoading(true);
    try {
      const result = await api.getCases({
        status: status || undefined,
        keyword: keyword || undefined,
        page: pagination.page,
        pageSize: pagination.pageSize,
      });
      setCases(result.data);
      setPagination((prev) => ({ ...prev, total: result.pagination.total }));
    } catch (error: any) {
      message.error(error.message || "获取案件列表失败");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCases();
  }, [pagination.page, pagination.pageSize, status]);

  const handleSearch = () => {
    setPagination((prev) => ({ ...prev, page: 1 }));
    fetchCases();
  };

  const columns = [
    {
      title: "案件编号",
      dataIndex: "case_no",
      key: "case_no",
      width: 180,
      render: (text: string) => (
        <span className="font-mono text-gray-600">{text}</span>
      ),
    },
    {
      title: "案件名称",
      dataIndex: "case_name",
      key: "case_name",
      ellipsis: true,
    },
    {
      title: "状态",
      dataIndex: "status",
      key: "status",
      width: 100,
      render: (status: keyof typeof statusMap) => (
        <Tag color={statusMap[status]?.color}>
          {statusMap[status]?.text}
        </Tag>
      ),
    },
    {
      title: "创建人",
      dataIndex: "creator_name",
      key: "creator_name",
      width: 100,
    },
    {
      title: "指派人",
      dataIndex: "assignee_name",
      key: "assignee_name",
      width: 100,
      render: (text: string) => text || "-",
    },
    {
      title: "创建时间",
      dataIndex: "created_at",
      key: "created_at",
      width: 180,
      render: (text: string) => dayjs(text).format("YYYY-MM-DD HH:mm"),
    },
    {
      title: "操作",
      key: "action",
      width: 150,
      render: (_: any, record: Case) => (
        <Space>
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => navigate(`/cases/${record.id}`)}
          >
            查看
          </Button>
          {record.status === "pending" && user?.role === "user_b" && (
            <Button
              type="link"
              size="small"
              icon={<EditOutlined />}
              onClick={() => navigate(`/cases/${record.id}/handle`)}
            >
              办理
            </Button>
          )}
        </Space>
      ),
    },
  ];

  const stats = {
    total: cases.length,
    draft: cases.filter((c) => c.status === "draft").length,
    pending: cases.filter((c) => c.status === "pending").length,
    archived: cases.filter((c) => c.status === "archived").length,
  };

  return (
    <div>
      <Row gutter={16} className="mb-6">
        <Col span={6}>
          <Card bordered={false}>
            <Statistic
              title="全部案件"
              value={stats.total}
              prefix={<FileTextOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card bordered={false}>
            <Statistic
              title="草稿"
              value={stats.draft}
              prefix={<EditOutlined style={{ color: "#999" }} />}
              valueStyle={{ color: "#999" }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card bordered={false}>
            <Statistic
              title="待处理"
              value={stats.pending}
              prefix={<ClockCircleOutlined style={{ color: "#1890ff" }} />}
              valueStyle={{ color: "#1890ff" }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card bordered={false}>
            <Statistic
              title="已归档"
              value={stats.archived}
              prefix={<CheckCircleOutlined style={{ color: "#52c41a" }} />}
              valueStyle={{ color: "#52c41a" }}
            />
          </Card>
        </Col>
      </Row>

      <Card bordered={false}>
        <div className="flex justify-between items-center mb-4">
          <Space>
            <Input
              placeholder="搜索案件名称/编号"
              prefix={<SearchOutlined />}
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              onPressEnter={handleSearch}
              style={{ width: 250 }}
              allowClear
            />
            <Select
              placeholder="状态筛选"
              value={status || undefined}
              onChange={(value) => setStatus(value || "")}
              style={{ width: 120 }}
              allowClear
              options={[
                { value: "draft", label: "草稿" },
                { value: "pending", label: "待处理" },
                { value: "archived", label: "已归档" },
              ]}
            />
            <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
              搜索
            </Button>
          </Space>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate("/cases/create")}
          >
            新建案件
          </Button>
        </div>

        <Table
          columns={columns}
          dataSource={cases}
          rowKey="id"
          loading={loading}
          pagination={{
            current: pagination.page,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条`,
            onChange: (page, pageSize) =>
              setPagination((prev) => ({ ...prev, page, pageSize })),
          }}
        />
      </Card>
    </div>
  );
}
